import PageHeader from "@/components/PageHeader";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { CtaBanner } from "@/components/CTA";
import { Section } from "@/pages/Home";
import Seo from "@/components/Seo";
import WhoGuidesDemo from "@/components/WhoGuidesDemo";
import { IMG, ImageBreak } from "@/components/ImageAssets";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin, Radar, ShieldCheck, Handshake, MessagesSquare, Activity,
  BarChart3, Compass, Users, LineChart, Mic, BrainCircuit, HeartHandshake, Repeat,
  Layers, Cpu, Network, Database, Lock, Sparkles, Rocket, Milestone, CheckCircle2, Circle,
  ArrowRight,
} from "lucide-react";

const features = [
  { icon: <MapPin />, title: "Secure Geo-Location Matching", body: "Match users with nearby verified resources based on need, location, eligibility, and service type — quickly and privately." },
  { icon: <Radar />, title: "AI-Assisted Resource Recommendations", body: "AI helps partners identify relevant support options — while human decision-making and partner accountability stay central." },
  { icon: <ShieldCheck />, title: "Verified Provider Directory", body: "A trusted partner network of agencies, nonprofits, healthcare providers, educational institutions, workforce partners, and community organizations." },
  { icon: <Handshake />, title: "Digital Warm Handoffs", body: "Partners initiate direct referrals with context, consent, and clear follow-up expectations — not just a phone number." },
  { icon: <MessagesSquare />, title: "Secure Messaging & Communication", body: "Authorized partners can coordinate securely to support the person, while protecting sensitive information." },
  { icon: <Activity />, title: "Referral Tracking & Follow-Up", body: "Every referral has a status: initiated, accepted, contacted, completed, or unresolved — visible to both sides." },
  { icon: <BarChart3 />, title: "Outcome Measurement Dashboards", body: "Dashboards help partners and funders understand whether people are actually being connected to services." },
  { icon: <Compass />, title: "Community Resource Mapping", body: "Identify service availability, geographic gaps, and community support capacity — in one view." },
  { icon: <Users />, title: "Partner Collaboration Tools", body: "Cross-agency coordination that reduces duplication and improves accountability." },
  { icon: <LineChart />, title: "Analytics & Performance Reporting", body: "Help public agencies, funders, and partners understand system performance and community needs." },
];

export default function Platform() {
  return (
    <div data-testid="platform-page">
      <PilotDemoBanner />
      <Seo
        title="Platform · WHO Guides | Coordination Infrastructure for Warm Handoffs"
        description="How the WHO platform works: WHO Guides™ (real people supported by AI), voice-first intake, verified partner network, warm handoffs, secure messaging, outcome dashboards, and community resource mapping."
        path="/platform"
      />
      <PageHeader
        eyebrow="Platform"
        title="Coordination infrastructure &mdash; engineered around the person."
        lede="WHO is not a database. It is a platform that coordinates every warm handoff from the first conversation to the confirmed outcome. WHO Guides&trade; &mdash; real people supported by nine specialist AI agents &mdash; listen, coordinate, and follow through. This is what the platform does."
      />

      {/* WHO GUIDES — full-page marquee experience */}
      <section
        className="relative overflow-hidden bg-navy-deep py-24 lg:py-32 border-b border-white/10"
        data-testid="platform-who-guides-section"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-gold/15 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-[640px] h-[640px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute inset-0 dot-grid opacity-[0.08]" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold" data-testid="platform-who-guides-eyebrow">
              WHO Guides · The Core Experience
            </p>
            <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.4rem]">
              You speak. A Guide answers. The handoff <span className="text-gold">actually happens</span>.
            </h2>
            <p className="mt-6 text-lg text-cream/80 leading-relaxed max-w-2xl">
              WHO Guides are the front door to the Relationship Operating System. Voice-native by
              design — because in a crisis, no one wants to fill out a form. AI coordinates the
              logistics. Real people stay accountable for the human moment.
            </p>
          </div>

          <div className="mt-14 grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  {
                    icon: <Mic className="w-5 h-5" />,
                    t: "Voice-Native Interface",
                    b: "Speak a real need in real language. The Guide transcribes, understands intent, and never asks the person to translate their pain into a category tree.",
                  },
                  {
                    icon: <BrainCircuit className="w-5 h-5" />,
                    t: "Agentic AI Orchestration",
                    b: "Nine specialist agents (Healthcare, Benefits, Housing, Employment, Education, Transportation, Family, Community, Financial) triage, match verified providers, prepare context for the receiving partner, and schedule follow-through — coordinated by one Guide.",
                  },
                  {
                    icon: <HeartHandshake className="w-5 h-5" />,
                    t: "Human-in-the-Loop",
                    b: "Every consequential moment involves a real WHO Guide. The AI moves the process. The person moves the outcome.",
                  },
                  {
                    icon: <Repeat className="w-5 h-5" />,
                    t: "Closed-Loop Follow-Through",
                    b: "Guides check back — with the person, with the partner — until the connection is confirmed. Referral becomes result.",
                  },
                ].map((f) => (
                  <div
                    key={f.t}
                    data-testid={`who-guides-pillar-${f.t.toLowerCase().replace(/\W+/g, "-")}`}
                    className="p-6 rounded-lg bg-white/[0.04] border border-white/10 hover:border-gold/40 transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-gold/15 text-gold">
                      {f.icon}
                    </span>
                    <h3 className="mt-4 font-display font-bold text-cream text-lg leading-tight">{f.t}</h3>
                    <p className="mt-2 text-cream/70 leading-relaxed text-sm">{f.b}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
                <PlatformStat value="1" label="Spoken need" />
                <PlatformStat value="0" label="Forms to fill out" />
                <PlatformStat value="∞" label="Loops actually closed" />
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2">
              <WhoGuidesDemo variant="full" />
            </div>
          </div>
        </div>
      </section>

      {/* WHO GUIDES PLATFORM ARCHITECTURE — full-width infographic */}
      <section
        className="relative py-24 lg:py-28 bg-cream-panel border-y border-borderSubtle overflow-hidden"
        data-testid="who-guides-architecture-section"
        id="architecture"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-navy/[0.05] blur-3xl" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold-deep" data-testid="architecture-eyebrow">
              Platform Architecture
            </p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.2rem]">
              From conversation to connection to outcomes.
            </h2>
            <p className="mt-5 text-lg text-slateSoft leading-relaxed">
              Seven components. One accountable loop. WHO Guides™ combines conversational AI, speech,
              workflow orchestration, a relationship graph, contextual matching, secure communications,
              and outcome analytics — so a spoken need becomes a warm handoff, and a warm handoff
              becomes a measurable outcome.
            </p>
          </div>

          {/* Infographic */}
          <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl border border-gold/20 bg-white">
            <img
              src="/assets/who-guides-architecture.png"
              alt="WHO Guides Platform Architecture — full system diagram. User entry via voice, text, or partner portal flows into: (1) Conversational AI — the WHO Guides Brain that understands natural language, remembers context, and coordinates next steps. (2) Speech AI — mature speech-to-text and natural text-to-speech. (3) AI Workflow Orchestration — the intelligent coordinator that executes multi-step workflows from start to finish. (4) Relationship Graph — every organization, provider, navigator, and agency connected in a secure graph. (5) AI Matching — intelligent, contextual matching based on branch, disability rating, employment history, family status, insurance, transportation, and more. (6) Secure Communications — accept referrals, schedule appointments, exchange documents, and chat securely. (7) Outcome Analytics — measurable impact tracked by referrals accepted, services initiated, successful completions, average response time, and days to connection. Bottom flow: Conversation → Warm Handoff → Continuous Connection → Measurable Outcome. Better Outcomes. Stronger Communities. One Conversation. One Connection. One Person at a Time."
              className="w-full h-auto block"
              loading="lazy"
              data-testid="who-guides-architecture-image"
            />
          </div>

          {/* Bottom flow summary */}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="architecture-flow">
            {[
              { n: "1", t: "Conversation", b: "The person speaks." },
              { n: "2", t: "Warm Handoff", b: "The Guide makes the intro." },
              { n: "3", t: "Continuous Connection™", b: "The loop stays open." },
              { n: "4", t: "Measurable Outcome", b: "Impact reported." },
            ].map((s) => (
              <div
                key={s.t}
                className="p-5 bg-white border border-borderSubtle rounded-lg"
                data-testid={`architecture-step-${s.n}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-navy text-cream font-display font-black flex items-center justify-center text-sm">
                    {s.n}
                  </span>
                  <p className="font-display font-bold text-navy">{s.t}</p>
                </div>
                <p className="mt-3 text-sm text-slateSoft leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTOR — 3-LAYER ARCHITECTURE STACK */}
      <section
        className="relative py-24 lg:py-32 bg-navy-deep text-cream border-y border-white/5 overflow-hidden"
        data-testid="three-layer-stack-section"
        id="stack"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute inset-0 dot-grid opacity-[0.06]" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold" data-testid="stack-eyebrow">The WHO Stack</p>
            <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.2rem]">
              Three layers. One accountable system.
            </h2>
            <p className="mt-6 text-lg text-cream/80 leading-relaxed">
              WHO is engineered as a stack: an <span className="text-gold">experience layer</span> people
              actually speak to, a <span className="text-gold">coordination layer</span> that turns need
              into action, and an <span className="text-gold">infrastructure layer</span> that keeps every
              connection verified, secure, and measurable.
            </p>
          </div>

          <div className="mt-14 space-y-6" data-testid="stack-layers">
            {[
              {
                n: "01",
                title: "Experience Layer",
                sub: "How the person meets WHO",
                accent: "cream",
                items: [
                  { icon: <Mic className="w-4 h-4" />,          t: "Voice-native intake",        b: "Speak the way you actually think — no forms, no jargon." },
                  { icon: <Users className="w-4 h-4" />,        t: "Mobile & partner portal",    b: "One conversation, three surfaces: mobile, web, and partner tools." },
                  { icon: <HeartHandshake className="w-4 h-4" />, t: "Human-in-the-loop",       b: "A real WHO Guide stays with the person through every consequential moment." },
                ],
              },
              {
                n: "02",
                title: "Coordination Layer",
                sub: "How WHO Guides orchestrate the handoff",
                accent: "gold",
                items: [
                  { icon: <BrainCircuit className="w-4 h-4" />, t: "Agentic AI orchestration",   b: "Nine specialist agents triage, match, and prepare context in parallel." },
                  { icon: <Handshake className="w-4 h-4" />,    t: "Warm handoff engine",        b: "Direct partner-to-partner intros with consent, ownership, and receipt." },
                  { icon: <Repeat className="w-4 h-4" />,       t: "Closed-loop follow-through", b: "Guides check back with the person and the partner until the connection is real." },
                ],
              },
              {
                n: "03",
                title: "Infrastructure Layer",
                sub: "How WHO stays verified, secure, and measurable",
                accent: "navy",
                items: [
                  { icon: <Network className="w-4 h-4" />,      t: "Verified partner graph",     b: "Every organization, provider, and Guide connected in a trusted relationship graph." },
                  { icon: <Lock className="w-4 h-4" />,         t: "Consent-first data & security", b: "Role-based access, encrypted communication, and consent at every step." },
                  { icon: <Database className="w-4 h-4" />,     t: "Outcome & signal platform",  b: "Every warm handoff generates measurable signal — the compounding moat." },
                ],
              },
            ].map((layer, li) => (
              <motion.div
                key={layer.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: li * 0.12, duration: 0.55 }}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 lg:p-10 hover:border-gold/40 transition-colors"
                data-testid={`stack-layer-${layer.n}`}
              >
                <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
                  <div className="lg:col-span-4">
                    <div className="flex items-baseline gap-3">
                      <span className="font-display font-black text-gold text-4xl leading-none">{layer.n}</span>
                      <span className="text-[10px] uppercase tracking-widest text-cream/50 font-bold">Layer {layer.n}</span>
                    </div>
                    <h3 className="mt-3 font-display font-black text-cream text-2xl lg:text-3xl leading-tight">
                      {layer.title}
                    </h3>
                    <p className="mt-2 text-cream/60 text-sm leading-relaxed">{layer.sub}</p>
                  </div>
                  <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
                    {layer.items.map((it) => (
                      <div
                        key={it.t}
                        className="p-5 rounded-lg bg-white/[0.04] border border-white/10 hover:border-gold/30 transition-colors"
                        data-testid={`stack-item-${it.t.toLowerCase().replace(/\W+/g, "-")}`}
                      >
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gold/15 text-gold">
                          {it.icon}
                        </span>
                        <p className="mt-3 font-display font-bold text-cream text-sm leading-tight">{it.t}</p>
                        <p className="mt-1.5 text-cream/65 text-xs leading-relaxed">{it.b}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom flow arrow line */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="stack-bottom-flow">
            {[
              { t: "The person speaks", i: <Mic className="w-4 h-4" /> },
              { t: "The Guide orchestrates", i: <Sparkles className="w-4 h-4" /> },
              { t: "The outcome is measured", i: <BarChart3 className="w-4 h-4" /> },
            ].map((s) => (
              <div key={s.t} className="p-4 rounded-md bg-gold/8 border border-gold/25 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gold text-navy">
                  {s.i}
                </span>
                <p className="font-display font-bold text-cream text-sm">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGY — GLASS CARDS */}
      <section
        className="relative py-24 lg:py-32 bg-navy text-cream overflow-hidden"
        data-testid="tech-glass-section"
        id="technology"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-gold/[0.09] blur-[120px]" />
          <div className="absolute inset-0 dot-grid opacity-[0.05]" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold" data-testid="tech-eyebrow">Technology</p>
            <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.2rem]">
              The engineering behind the <span className="text-gold">warm handoff</span>.
            </h2>
            <p className="mt-6 text-lg text-cream/80 leading-relaxed">
              WHO Guides are backed by a production-grade stack — speech AI, agentic reasoning, a
              verified relationship graph, and outcome signal. Each capability is composable, each
              one hardened for public-sector, healthcare, and community-based deployment.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tech-glass-grid">
            {[
              {
                icon: <Mic className="w-5 h-5" />,
                t: "Voice AI · Speech Layer",
                b: "Low-latency speech-to-text and natural, human-cadence text-to-speech, tuned for clarity, trust, and accessibility.",
                tag: "STT · TTS",
              },
              {
                icon: <BrainCircuit className="w-5 h-5" />,
                t: "LLM Reasoning Core",
                b: "Frontier reasoning models coordinate intent, eligibility, and next-best-action — with human-in-the-loop guardrails.",
                tag: "Agentic AI",
              },
              {
                icon: <Cpu className="w-5 h-5" />,
                t: "Agent Orchestration",
                b: "Nine specialist agents (Healthcare, Benefits, Housing, Employment, Education, Transportation, Family, Community, Financial) working in parallel.",
                tag: "9 Specialist Agents",
              },
              {
                icon: <Network className="w-5 h-5" />,
                t: "Relationship Graph",
                b: "A verified graph of organizations, providers, Guides, and referrals — with edge-level trust, capacity, and outcome data.",
                tag: "Graph Data Model",
              },
              {
                icon: <ShieldCheck className="w-5 h-5" />,
                t: "Consent-First Security",
                b: "Role-based access, encrypted communication, audit trails, and consent captured at every meaningful boundary.",
                tag: "Trust by Design",
              },
              {
                icon: <BarChart3 className="w-5 h-5" />,
                t: "Outcome Signal Platform",
                b: "Every referral produces status. Every status produces signal. Signal becomes insight for partners, funders, and policy.",
                tag: "Measurable Impact",
              },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                className="relative p-7 rounded-2xl bg-white/[0.05] backdrop-blur-xl border border-white/15 hover:border-gold/50 hover:bg-white/[0.07] transition-all group overflow-hidden"
                data-testid={`tech-glass-card-${i}`}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.06] via-transparent to-transparent pointer-events-none" aria-hidden />
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-gold/15 border border-gold/30 text-gold group-hover:bg-gold group-hover:text-navy transition-colors">
                      {c.icon}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-gold/80 font-bold">{c.tag}</span>
                  </div>
                  <h3 className="mt-6 font-display font-bold text-cream text-lg leading-tight">{c.t}</h3>
                  <p className="mt-3 text-cream/70 leading-relaxed text-sm">{c.b}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-10 text-sm text-cream/50 max-w-3xl leading-relaxed">
            Every component is designed to be interoperable with existing case management, EHR, and CRM systems.
            WHO extends the systems partners already rely on — it does not replace them.
          </p>
        </div>
      </section>

      {/* Mobile App Showcase */}
      {/* WHO Guides™ — coordinated support for complex needs (planned capability) */}
      <section className="py-16 lg:py-20 bg-navy-deep text-cream border-y border-white/5" data-testid="platform-guides-multi-need-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-bold border bg-white/5 text-cream/70 border-white/15" data-testid="platform-guides-planned-label">
            WHO Guides™ — Planned Coordinated Support Capability
          </span>
          <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.05] text-3xl sm:text-4xl">
            One Veteran. Multiple Needs. A More Connected Path Forward.
          </h2>
          <div className="mt-6 space-y-4 text-cream/80 leading-relaxed max-w-3xl">
            <p>Starting a business often requires more than a single referral. A veteran entrepreneur may need business counseling, financing guidance, certification assistance, and access to government contracting opportunities — sometimes from different programs within the same organization.</p>
            <p>WHO Guides™ are being designed to recognize these related needs, help identify appropriate resources, and support meaningful connections when active coordination is appropriate and authorized.</p>
            <p>The intended approach recognizes that receiving information, connecting with a service provider, and achieving a business milestone are different outcomes. WHO&rsquo;s goal is to help veterans navigate those steps without losing visibility of unresolved needs.</p>
          </div>
          <Link to="/discovery?category=veteran_entrepreneurship" className="mt-8 inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors" data-testid="platform-guides-entrepreneurship-cta">
            Explore Entrepreneurship Resources <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
        </div>
      </section>

      <Section eyebrow="The WHO Mobile App" title="Support in the palm of the person who needs it.">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative order-2 lg:order-1">
            <div className="relative flex justify-center">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden>
                <div className="w-[520px] h-[520px] rounded-full bg-gold/10" />
              </div>
              <img
                src="/assets/who-app-mockup-2.png"
                alt="WHO mobile app home screen showing personalized support connections, warm handoffs, follow-through tracking, and 988 crisis support"
                className="relative w-full max-w-[420px] h-auto drop-shadow-2xl"
                data-testid="platform-app-mockup"
                loading="lazy"
              />
            </div>
            <div className="hidden lg:flex absolute top-14 -left-2 bg-white border border-borderSubtle rounded-md px-4 py-3 shadow-md items-center gap-3" data-testid="mockup-annotation-welcome">
              <span className="w-2 h-2 rounded-full bg-gold" />
              <span className="text-sm font-semibold text-navy">Personalized welcome</span>
            </div>
            <div className="hidden lg:flex absolute top-1/3 -right-2 bg-white border border-borderSubtle rounded-md px-4 py-3 shadow-md items-center gap-3" data-testid="mockup-annotation-categories">
              <span className="w-2 h-2 rounded-full bg-navy" />
              <span className="text-sm font-semibold text-navy">Explore support categories</span>
            </div>
            <div className="hidden lg:flex absolute bottom-24 -left-4 bg-white border border-borderSubtle rounded-md px-4 py-3 shadow-md items-center gap-3" data-testid="mockup-annotation-crisis">
              <span className="w-2 h-2 rounded-full bg-gold" />
              <span className="text-sm font-semibold text-navy">988 crisis support · always visible</span>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="text-lg text-slateSoft leading-relaxed">
              The WHO mobile app puts warm-handoff infrastructure directly in the hands of the
              people who need it — with clarity, dignity, and immediate access to trusted
              organizations.
            </p>
            <ul className="mt-8 space-y-5">
              {[
                { t: "Find Support Near You", b: "Geo-located, verified providers matched to the person's need and eligibility." },
                { t: "Talk to Someone", b: "Direct, secure connection to a real human at a trusted organization — not a phone tree." },
                { t: "Personalized Connections", b: "Trusted introductions to organizations, not a search page of unknown links." },
                { t: "Warm Hand Off", b: "You're introduced directly — you're not starting over with every new provider." },
                { t: "Follow Through", b: "The app follows up to make sure the help actually happened." },
                { t: "Track Outcomes", b: "Progress is visible. Next steps are clear. Nothing falls between the cracks." },
                { t: "988 Crisis Support", b: "Immediate, always-visible access to crisis lifelines when it matters most." },
              ].map((f) => (
                <li key={f.t} className="flex gap-4" data-testid={`platform-app-feature-${f.t.toLowerCase().replace(/\W+/g, "-")}`}>
                  <span className="mt-2 w-2.5 h-2.5 rounded-full bg-gold shrink-0" />
                  <div>
                    <p className="font-display font-bold text-navy text-lg leading-tight">{f.t}</p>
                    <p className="mt-1 text-slateSoft leading-relaxed">{f.b}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section eyebrow="What WHO does" title="Technology plus human accountability." bg="bone">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {features.map((f, i) => {
            // Bento tetris: alternate spans
            const spans = [
              "md:col-span-3 md:row-span-2",
              "md:col-span-3",
              "md:col-span-2",
              "md:col-span-2",
              "md:col-span-2",
              "md:col-span-3",
              "md:col-span-3",
              "md:col-span-2",
              "md:col-span-2",
              "md:col-span-2",
            ];
            const accent = i === 0 || i === 6;
            return (
              <div
                key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/\W+/g, "-")}`}
                className={`${spans[i]} relative overflow-hidden bg-white border border-borderSubtle rounded-lg p-7 hover:shadow-md transition-shadow`}
              >
                {accent && <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10" aria-hidden />}
                <span className={`inline-flex w-11 h-11 items-center justify-center rounded-md ${accent ? "bg-navy text-gold" : "bg-navy-50 text-navy-800"}`}>
                  {f.icon}
                </span>
                <h3 className="mt-5 font-display font-bold text-navy-900 text-xl leading-tight">
                  {String(i + 1).padStart(2, "0")} · {f.title}
                </h3>
                <p className="mt-3 text-slateSoft leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section eyebrow="Why it works" title="Because a system built for accountability changes what&#39;s possible.">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: "Secure by design", b: "Consent-based data sharing, role-based access, and encrypted communication protect the people we serve." },
            { t: "Interoperable", b: "Built to work alongside existing case management, EHR, and CRM systems — not replace them." },
            { t: "Public-sector ready", b: "Designed for agencies, healthcare systems, universities, and funders that require credible reporting." },
          ].map((p) => (
            <div key={p.t} className="p-7 bg-white border border-borderSubtle rounded-lg" data-testid={`principle-${p.t.toLowerCase().replace(/\W+/g, "-")}`}>
              <h3 className="font-display font-bold text-navy-900 text-lg">{p.t}</h3>
              <p className="mt-3 text-slateSoft leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <ImageBreak
        src={IMG.networkNodes}
        alt="Illuminated network nodes connecting across a map"
        eyebrow="One connected network"
        caption="Every organization. Every provider. Every warm handoff — visible."
        testId="platform-image-break"
        height="h-[320px] md:h-[380px]"
      />

      {/* PRODUCT ROADMAP — the trajectory */}
      <section
        className="relative py-24 lg:py-32 bg-cream text-navy border-t border-borderSubtle overflow-hidden"
        data-testid="roadmap-section"
        id="roadmap"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/12 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-navy/[0.04] blur-3xl" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold-deep" data-testid="roadmap-eyebrow">
              Product Roadmap
            </p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.2rem]">
              From Pittsburgh pilot to <span className="text-gold-deep">national infrastructure</span>.
            </h2>
            <p className="mt-6 text-lg text-slateSoft leading-relaxed">
              WHO is being built in deliberate phases — proving warm handoffs in one region before
              extending the model to more communities, more partners, and eventually a public-good
              data platform for the transition ecosystem.
            </p>
          </div>

          {/* Timeline */}
          <div className="mt-14 relative" data-testid="roadmap-timeline">
            {/* vertical spine on md+, horizontal band on lg+ */}
            <div className="absolute left-4 top-2 bottom-2 w-[2px] bg-gold/30 lg:hidden" aria-hidden />
            <div className="hidden lg:block absolute left-0 right-0 top-[38px] h-[2px] bg-gold/30" aria-hidden />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-4">
              {[
                {
                  q: "Q4 2025",
                  title: "Foundation",
                  status: "Complete",
                  done: true,
                  icon: <CheckCircle2 className="w-4 h-4" />,
                  items: [
                    "Platform architecture defined",
                    "WHO Guides™ agentic AI framework",
                    "Founding partner conversations",
                  ],
                },
                {
                  q: "Q1 2026",
                  title: "Pittsburgh Pilot",
                  status: "In progress",
                  done: false,
                  active: true,
                  icon: <Rocket className="w-4 h-4" />,
                  items: [
                    "Live warm-handoff loops",
                    "Verified provider onboarding",
                    "First outcome data captured",
                  ],
                },
                {
                  q: "Q3 2026",
                  title: "Multi-Region Expansion",
                  status: "Planned",
                  done: false,
                  icon: <Milestone className="w-4 h-4" />,
                  items: [
                    "2–3 additional metros",
                    "Partner playbooks & training",
                    "Public-sector reporting v1",
                  ],
                },
                {
                  q: "Q1 2027",
                  title: "National Partner Network",
                  status: "Planned",
                  done: false,
                  icon: <Milestone className="w-4 h-4" />,
                  items: [
                    "Federal + state agency integrations",
                    "Healthcare & workforce connectors",
                    "Cross-region referral routing",
                  ],
                },
                {
                  q: "Q3 2027+",
                  title: "Data Platform for Policy",
                  status: "Vision",
                  done: false,
                  icon: <Sparkles className="w-4 h-4" />,
                  items: [
                    "Aggregated outcome insights",
                    "Research & policy dashboards",
                    "The compounding coordination moat",
                  ],
                },
              ].map((p, i) => (
                <motion.div
                  key={p.q}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="relative pl-12 lg:pl-0"
                  data-testid={`roadmap-phase-${i}`}
                >
                  {/* Node */}
                  <div className="absolute left-0 top-2 lg:static lg:mb-4">
                    <div className="lg:flex lg:items-center lg:justify-start">
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full ring-4 ring-cream ${
                          p.done
                            ? "bg-gold text-navy"
                            : p.active
                              ? "bg-navy text-gold animate-pulse"
                              : "bg-white text-navy border-2 border-gold/50"
                        }`}
                      >
                        {p.icon}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`p-6 rounded-lg border transition-colors ${
                      p.active
                        ? "bg-navy text-cream border-gold/50 shadow-xl"
                        : "bg-white text-navy border-borderSubtle hover:border-gold/40"
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-[10px] uppercase tracking-widest font-bold ${p.active ? "text-gold" : "text-gold-deep"}`}>
                        {p.q}
                      </p>
                      <span
                        className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                          p.done
                            ? "bg-gold/20 text-gold-deep border border-gold/40"
                            : p.active
                              ? "bg-gold text-navy"
                              : "bg-navy/[0.06] text-slateSoft border border-navy/10"
                        }`}
                      >
                        {p.status}
                      </span>
                    </div>
                    <h3 className={`mt-3 font-display font-black text-lg leading-tight ${p.active ? "text-cream" : "text-navy"}`}>
                      {p.title}
                    </h3>
                    <ul className={`mt-4 space-y-2 text-sm leading-relaxed ${p.active ? "text-cream/85" : "text-slateSoft"}`}>
                      {p.items.map((li) => (
                        <li key={li} className="flex gap-2">
                          <Circle className={`w-2 h-2 mt-1.5 shrink-0 ${p.active ? "text-gold fill-gold" : "text-gold-deep fill-gold-deep"}`} />
                          <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Callout */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mt-14 grid lg:grid-cols-12 gap-6 items-center rounded-2xl p-8 lg:p-10 bg-navy-deep text-cream border border-gold/40"
            data-testid="roadmap-callout"
          >
            <div className="lg:col-span-8">
              <p className="eyebrow text-gold">Where we are now</p>
              <p className="mt-3 font-display font-black text-cream text-2xl lg:text-3xl leading-tight">
                Pittsburgh pilot &mdash; live loops, real partners, first outcome data.
              </p>
              <p className="mt-3 text-cream/80 leading-relaxed">
                We are actively signing pilot partners, verifying providers, and closing the first warm-handoff
                loops. If your organization serves veterans, transitioning service members, or families —
                the roadmap has a seat for you.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <a
                href="/contact"
                data-testid="roadmap-cta-join"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Join the Pilot
              </a>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-cream/50">Pittsburgh · Q1 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      <CtaBanner
        eyebrow="See it in action."
        title="Request a walkthrough tailored to your organization."
        primaryText="Request a Demo"
        primaryTo="/contact"
        secondaryText="Join the Network"
        secondaryTo="/partners"
      />
    </div>
  );
}

function PlatformStat({ value, label }) {
  return (
    <div data-testid={`platform-stat-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className="font-display font-black text-gold text-4xl leading-none">{value}</div>
      <div className="mt-2 text-[11px] uppercase tracking-widest text-cream/70">{label}</div>
    </div>
  );
}
